import {useEffect, useState} from 'react'
import {useLayout} from '../../../../_metronic/layout/core'
import {
  createStockOut,
  getWarehouseOptions,
  getProductOptions,
  getCurrentInventoryMetrics,
  WarehouseOption,
  ProductOption,
} from '../api'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import toast from 'react-hot-toast'
import Select from 'react-select'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {InventoryStateSummary} from '../../inventory/components/InventoryStateSummary'
import {removeIndicatorSeparator, solidSelectStyles} from '../../shared/ui/selectStyles'

// ─── Validation Schema ────────────────────────────────────────────────────────

const stockOutSchema = Yup.object().shape({
  warehouse_id: Yup.number().min(1, 'Warehouse is required').required('Warehouse is required'),
  product_id: Yup.number().min(1, 'Product is required').required('Product is required'),
  destination: Yup.string().required('Destination is required'),
  quantity: Yup.number().min(1, 'Quantity must be at least 1').required('Quantity is required'),
  reference_number: Yup.string().required('Reference number is required'),
  exit_date: Yup.string().required('Date of removal is required'),
})

// ─── Constants ────────────────────────────────────────────────────────────────

const DESTINATION_TYPES = ['Sales', 'Scrap', 'Others']

// ─── Types ────────────────────────────────────────────────────────────────────

interface StockOutFormValues {
  warehouse_id: number
  product_id: number
  destination: string
  quantity: number
  reference_number: string
  exit_date: string
}

type OptionType = {
  value: number
  label: string
}

const getTodayDate = () => new Date().toISOString().split('T')[0]

// ─── Component ────────────────────────────────────────────────────────────────

export const StockOut: React.FC = () => {
  const {company} = useLayout()
  const [loading, setLoading] = useState(false)
  const [warehouseOptions, setWarehouseOptions] = useState<OptionType[]>([])
  const [productOptions, setProductOptions] = useState<OptionType[]>([])
  const [warehouse, setWarehouse] = useState<OptionType>({label: 'Select Warehouse...', value: 0})
  const [product, setProduct] = useState<OptionType>({label: 'Select Product...', value: 0})
  const [availableStock, setAvailableStock] = useState<number>(0)
  const [valuationPerUnit, setValuationPerUnit] = useState<number>(0)

  const initialValues: StockOutFormValues = {
    warehouse_id: 0,
    product_id: 0,
    destination: 'Sales',
    quantity: 0,
    reference_number: '',
    exit_date: getTodayDate(),
  }

  const formik = useFormik<StockOutFormValues>({
    initialValues,
    validationSchema: stockOutSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      if (values.quantity > availableStock) {
        toast.error('Quantity exceeds available stock.')
        return
      }
      setLoading(true)
      const body = {
        stock_out_id: null,
        company_id: Number(company?.value) || null,
        warehouse_id: values.warehouse_id,
        product_id: values.product_id,
        quantity: values.quantity,
        destination: values.destination.toLowerCase(),
        reference_number: values.reference_number,
        document_id: null,
        exit_date: values.exit_date,
      }
      await createStockOut(body)
        .then((res) => {
          res?.detail && toast.success('Stock Out has been recorded successfully.')
          formik.resetForm()
          setWarehouse({label: 'Select Warehouse...', value: 0})
          setProduct({label: 'Select Product...', value: 0})
          setAvailableStock(0)
          setValuationPerUnit(0)
        })
        .catch((err) => toast.error(err?.detail || err.message))
        .finally(() => setLoading(false))
    },
  })

  // Load warehouse and product options
  useEffect(() => {
    const loadOptions = async () => {
      if (!company?.value) return

      const [whRes, prodRes] = await Promise.all([
        getWarehouseOptions(company.value),
        getProductOptions(company.value),
      ])
      if (whRes) {
        setWarehouseOptions(
          whRes.map((w: WarehouseOption) => ({label: w.warehouse_name, value: w.warehouse_id}))
        )
      }
      if (prodRes) {
        setProductOptions(
          prodRes.map((p: ProductOption) => ({label: p.product_name, value: p.product_id}))
        )
      }
    }

    loadOptions()
  }, [company?.value])

  // Sync selects back into formik
  useEffect(() => {
    formik.setFieldValue('warehouse_id', warehouse.value, true)
    formik.setFieldValue('product_id', product.value, true)
  }, [warehouse.value, product.value])

  // Calculate available stock when warehouse + product both selected
  useEffect(() => {
    const calculateAvailableStock = async () => {
      if (!company?.value || !warehouse.value || !product.value) {
        setAvailableStock(0)
        setValuationPerUnit(0)
        return
      }
      try {
        const metrics = await getCurrentInventoryMetrics(
          company.value,
          Number(warehouse.value),
          Number(product.value)
        )
        if (metrics) {
          setAvailableStock(Number(metrics.available_stock || 0))
          setValuationPerUnit(Number(metrics.valuation_per_unit || 0))
        }
      } catch {
        setAvailableStock(0)
        setValuationPerUnit(0)
      }
    }
    calculateAvailableStock()
  }, [warehouse.value, product.value, company?.value])

  return (
    <KTCard>
      <div className='card-header border-0'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Stock Out</h3>
        </div>
      </div>

      <KTCardBody className='py-4'>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          {/* Warehouse + Product + Available Stock */}
          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Warehouse</label>
            <div className='col-lg-4 fv-row'>
              <Select
                value={warehouse}
                onChange={(option: any) => setWarehouse(option as OptionType)}
                options={warehouseOptions}
                isSearchable={true}
                className='fw-bold'
                styles={solidSelectStyles}
                components={removeIndicatorSeparator}
              />
            </div>

            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Product</label>
            <div className='col-lg-4 fv-row'>
              <Select
                value={product}
                onChange={(option: any) => setProduct(option as OptionType)}
                options={productOptions}
                isSearchable={true}
                className='fw-bold'
                styles={solidSelectStyles}
                components={removeIndicatorSeparator}
              />
            </div>
          </div>

          {/* Available Stock (readonly display) */}
          <InventoryStateSummary
            stockLabel='Available Stock'
            availableStock={availableStock}
            valuationPerUnit={valuationPerUnit}
          />

          {/* Destination */}
          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Destination</label>
            <div className='col-lg-10 fv-row d-flex gap-8 align-items-center pt-2'>
              {DESTINATION_TYPES.map((t) => (
                <span key={t} className='form-check form-check-custom form-check-solid fs-6'>
                  {t}{' '}
                  <input
                    className='form-check-input'
                    type='radio'
                    name='destination'
                    value={t}
                    checked={formik.values.destination === t}
                    onChange={() => formik.setFieldValue('destination', t)}
                    style={{marginLeft: '8px'}}
                  />
                </span>
              ))}
            </div>
          </div>

          {/* Quantity + Reference */}
          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Quantity</label>
            <div className='col-lg-4 fv-row'>
              <input
                type='number'
                className='form-control form-control-lg form-control-solid'
                placeholder='Quantity'
                {...formik.getFieldProps('quantity')}
              />
              {formik.touched.quantity && formik.errors.quantity && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.quantity}</div>
                </div>
              )}
            </div>

            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Reference No.</label>
            <div className='col-lg-4 fv-row'>
              <input
                type='text'
                className='form-control form-control-lg form-control-solid'
                placeholder='Reference Number'
                {...formik.getFieldProps('reference_number')}
              />
              {formik.touched.reference_number && formik.errors.reference_number && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.reference_number}</div>
                </div>
              )}
            </div>
          </div>

          {/* Date of Removal + Upload */}
          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Date of Removal</label>
            <div className='col-lg-4 fv-row'>
              <input
                type='date'
                className='form-control form-control-lg form-control-solid'
                {...formik.getFieldProps('exit_date')}
              />
              {formik.touched.exit_date && formik.errors.exit_date && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.exit_date}</div>
                </div>
              )}
            </div>

            <label className='col-lg-2 col-form-label fw-bold fs-6'>Upload Document</label>
            <div className='col-lg-4 fv-row'>
              <input type='file' className='form-control form-control-lg form-control-solid' />
            </div>
          </div>

          <div className='card-footer d-flex justify-content-start px-0 border-0 pb-0 pt-4'>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={loading || !formik.isValid || formik.values.quantity > availableStock}
            >
              {!loading ? 'Record Stock Out' : null}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </KTCardBody>
    </KTCard>
  )
}
